import {PhonesComponent} from '../phones/phones.component';
import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {timeZonesData} from '../../config/data/time-zones/time-zones.data';
import {fadeInAnimation} from '../../animations/fade-in.animation';
import {AddressSectionComponent} from '../address-section/address-section.component';
import {NgForm, UntypedFormControl, Validators} from '@angular/forms';
import {HelperValidationField} from '../../class/helper/helper-validation-field.class';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {Store} from '@ngxs/store';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {Loader} from '../../models/loader/loader.redux';
import {debounceTime} from 'rxjs/operators';
import {firstValueFrom, Subscription} from 'rxjs';
import {Logger} from '../../class/helper/logger.class';
import {ICardIconText} from '../../interfaces/card';
import {cloneDeep} from 'lodash';
import {PLATFORM_CONFIG} from '../../config/platform.config';
import {IPatient} from '../../models/patient';
import {IUser} from '../../models/auth';
import {GenderEnum, UserAction, UserState} from '../../models/user';

@Component({
  selector: 'app-modal-create-patient',
  templateUrl: './modal-create-patient.component.html',
  styleUrls: ['./modal-create-patient.component.scss'],
  animations: [fadeInAnimation]
})
export class ModalCreatePatientComponent implements OnInit, OnDestroy {
  @Input() notClosable: boolean;
  @Input() completeData: boolean;
  @Input() patient: IPatient;
  @Input() userPatient: IUser;
  @Input() clinicId: string;
  // Modal actions
  @Input() isVisibleModal: boolean;
  @Input() onlyRegister: boolean;
  @Output() closeModal: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('userForm') userForm: NgForm;
  @ViewChild('checkEmailForm') checkEmailForm: NgForm;
  @ViewChild(PhonesComponent) phonesComponent: PhonesComponent;
  @ViewChild(AddressSectionComponent) addressSectionComponent: AddressSectionComponent;

  helperValidationField: HelperValidationField;
  timeZones: string[];
  genderEnum = GenderEnum;

  modeInvitation = true;
  checkEmail = true;
  emailControl = new UntypedFormControl('', [Validators.required]);
  userExists: boolean;
  emailControlSubscription: Subscription;
  registerTypes: ICardIconText[];
  registerTypeSelected: string;
  showAddressAndPhone: boolean;

  platformConfig = PLATFORM_CONFIG;

  constructor(
    public utilitiesServices: UtilitiesService,
    private store: Store,
    private messageAlertService: MessageAlertService
  ) {
    this.emailControlSubscription = this.emailControl.valueChanges.pipe(debounceTime(500)).subscribe(async text => {
      if (!this.emailControl.valid) return;
      if (!this.userPatient) {
        await this.onCheckEmail();
      }
      this.patient.user.email = text;
    });
  }

  /**
   * Inicializa las propiedades necesarias.
   */
  ngOnInit(): void {
    this.registerTypes = [
      {
        id: 'invitation',
        label: 'Invitación',
        img: './../../../assets/images/icons/icons8-email-100 2.png'
      },
      {
        id: 'register',
        label: 'Registro',
        img: './../../../assets/images/icons/icons8-survey-100.png'
      }
    ];
    this.helperValidationField = new HelperValidationField();
    this.userExists = false;
    this.checkPatient();
    this.timeZones = timeZonesData.nameTimeZones();
  }

  /**
   * Cambia el modo de registro del paciente.
   * @param type
   */
  onChangeRegisterType(type: ICardIconText): void {
    this.registerTypeSelected = type.id;
  }

  // tslint:disable:use-lifecycle-interface
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isVisibleModal?.currentValue === false) {
      this.checkPatient();
    }
  }

  /**
   * Emite al componente padre cuando se cierra el modal
   * @param action
   */
  onEmitCloseModal(action: boolean): void {
    if (action && this.phonesComponent && this.phonesComponent.isValid()) {
      // this.patient.user.phones = this.phoneSectionComponent.getFormValueWithPlaceholders();
      this.patient.user.phones = this.phonesComponent.getRawValue();
    }
    if (!action) {
      setTimeout(() => {
        this.resetForms();
        this.emailControl.reset();
        this.emailControl.markAsUntouched();
        this.modeInvitation = true;
        this.checkEmail = true;
        this.userExists = false;
        this.registerTypeSelected = null;
      }, 100);
    }

    this.closeModal.emit(action);
  }

  /**
   * Touch all forms
   */
  formMarkAllAsTouched(): void {
    this.userForm.form.markAllAsTouched();
    this.emailControl.markAllAsTouched();
    this.emailControl.markAsDirty();
    this.addressSectionComponent?.formAddress?.form?.markAllAsTouched();
    this.phonesComponent?.isValid();
  }

  /**
   * Check that forms userForm, phoneSectionComponent and addressSectionComponent are valid
   * @return boolean
   */
  areValidForms(): boolean {
    if (this.addressSectionComponent && this.phonesComponent) {
      return this.userForm.valid && this.addressSectionComponent?.formAddress?.valid && this.phonesComponent.isValid();
    } else {
      return this.userForm.valid;
    }
  }

  /**
   * Get clinicBranch values
   * @return IClinicBranch
   * @note need to add the clinicId property externally
   */
  getValue(): IUser {
    return {
      ...this.userForm.form.value,
      address: this.addressSectionComponent?.formAddress?.value,
      phones: this.phonesComponent?.getRawValue(),
    };
  }

  /**
   * Check obj clinicBranch
   * @private
   */
  private checkPatient(reset: boolean = false): void {
    this.patient = cloneDeep(reset ? null : this.patient);
    this.patient = cloneDeep(this.patient ?? {
      clinicId: null,
      userId: null,
      observationNoteId: '',
      tags: [],
      user: {
        name: '',
        lastname: '',
        password: '',
        email: '',
        timeZone: this.utilitiesServices.getTimeZoneBrowser(),
        address: {
          country: '',
          reference: '',
          state: '',
          street: '',
          cp: undefined,
          suburb: '',
          city: ''
        },
        phones: []
      }
    });
    if (this.userPatient) {
      this.patient.user = { ...this.userPatient };
      this.emailControl.setValue(this.userPatient.email);
      this.userExists = false;
    }
  }

  /**
   * Clean patient forms
   */
  resetForms(): void {
    if (this.userForm) {
      this.userForm.form.reset();
    }
    if (this.addressSectionComponent) {
      this.addressSectionComponent?.formAddress?.resetForm();
    }
    this.checkPatient(true);
  }

  /**
   * Verifica que el email exista ya en la plataforma.
   */
  async onCheckEmail(): Promise<void> {
    try {
      await firstValueFrom((this.store.dispatch(new UserAction.Exist(this.emailControl.value))));
      this.userExists = cloneDeep(await firstValueFrom((this.store.selectOnce(UserState.getUserExists))));
      this.checkEmail = this.userExists;
    } catch (e) {
      Logger.log('Ocurrió un error', e?.message);
      this.checkEmail = false;
    }
  }

  /**
   * Envía una invitación al paciente.
   */
  async onInvite(): Promise<void> {
    try {
      await firstValueFrom((this.store.dispatch(new Loader(true, 'Enviando invitación...'))));
      await firstValueFrom((this.store.dispatch(new UserAction.Invite(this.clinicId, this.emailControl.value))));
      this.messageAlertService.createMessage('success', 'Se ha enviado un correo de invitación al paciente');
      this.onEmitCloseModal(false);
    } catch (e) {
      Logger.log('Ocurrió un error', e?.message);
      let messageError = '';
      if (e.message === 'An administrator cannot be invited as a patient in your clinic') {
        messageError = 'No se puede invitar a un administrador como paciente en su clínica';
      }
      this.messageAlertService.createMessage('error', messageError ? messageError : 'No se ha podido invitar al usuario.', 5000);
    }
    this.checkEmail = true;
    await firstValueFrom((this.store.dispatch(new Loader(false))));
  }

  ngOnDestroy(): void {
    this.emailControlSubscription.unsubscribe();
  }


}
